document.addEventListener("DOMContentLoaded", () => {
  /* =========================================================
     HELPERS
  ========================================================= */
  const normalize = (val) => {
    if (!val) return "";
    return val
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-");
  };

  /**
   * ✅ CONFIG: add/remove standards here only
   * token must match what appears in the group id/aria-controls after normalize()
   * e.g. "filter-group-filter-p-m-custom-en_511_convective_cold-sidebar" -> contains "en-511"
   */
  const EN_STANDARDS = [
    { key: "en388", token: "en-388" },
    { key: "en407", token: "en-407" },
    { key: "en511", token: "en-511" },
    { key: "en374", token: "en-374" },
  ];

  const TOKENS_BY_KEY = Object.fromEntries(EN_STANDARDS.map((s) => [s.key, s.token]));

  // ✅ reliable token for a filter group (works with en_511 in aria-controls)
  const getGroupToken = (group) => {
    const toggle = group.querySelector("[data-filter-group-toggle]");
    const id =
      toggle?.getAttribute("aria-controls") ||
      toggle?.getAttribute("data-filter-group-toggle") ||
      group.getAttribute("id") ||
      group.dataset.filterLabel ||
      "";
    return normalize(id);
  };

  /* =========================================================
     PER-STANDARD STATE
  ========================================================= */
  const state = {};
  EN_STANDARDS.forEach(({ key }) => {
    state[key] = {
      isProcessing: false,
      nextAllowed: 0,
      children: [],
    };
  });

  /* =========================================================
     OPEN FAKE PARENT
  ========================================================= */
  function openFakeParentIfActive(standardKey) {
    const token = TOKENS_BY_KEY[standardKey];

    const hasActive =
    // If any fake option is active, we should open the fake list
    !!document.querySelector(`.en-parent-btn[data-en-standard="${standardKey}"].active`) ||

    // Otherwise fall back to real groups
    Array.from(document.querySelectorAll(".en-child-group")).some((group) => {
      const groupToken = getGroupToken(group);
      if (!groupToken.includes(token)) return false;

      if (group.getAttribute("data-filter-group-has-active-filters") === "true") return true;
      return !!group.querySelector("input:checked");
    });

    if (!hasActive) return;

    const parentWrapper = document.querySelector(
      `[data-en-parent-group="true"][data-en-standard="${standardKey}"]`
    );

    if (!parentWrapper) return;

    const list =
      parentWrapper.querySelector(`[data-en-list="true"][data-en-standard="${standardKey}"]`) ||
      parentWrapper.querySelector(".en-parent-list");

    const toggle =
      parentWrapper.querySelector(`[data-en-toggle="true"][data-en-standard="${standardKey}"]`) ||
      parentWrapper.querySelector(".en-parent-toggle");

    if (!list || list.classList.contains("is-open")) return;

    list.classList.add("is-open");
    list.setAttribute("aria-hidden", "false");
    if (toggle) {
      toggle.classList.add("is-open");
      toggle.setAttribute("aria-expanded", "true");
    }

    // Animate open (match your click logic)
    const h = list.scrollHeight;
    list.style.height = "0px";
    requestAnimationFrame(() => {
      list.style.height = `${h}px`;
    });
    setTimeout(() => {
      if (list.classList.contains("is-open")) list.style.height = "auto";
    }, 300);
  }

  /* =========================================================
     RESET VISIBILITY — based on real child checkboxes
  ========================================================= */
  function updateResetVisibility(standardKey) {
    const token = TOKENS_BY_KEY[standardKey];

    // Active = any EN child group for this standard has active filters
    const hasActive = Array.from(document.querySelectorAll(".en-child-group")).some((group) => {
      const groupToken = getGroupToken(group);
      if (!groupToken.includes(token)) return false;

      if (group.getAttribute("data-filter-group-has-active-filters") === "true") return true;
      return !!group.querySelector("input:checked");
    });

    // Toggle reset wrapper active
    const resetButtons = document.querySelectorAll(
      `[data-en-reset="true"][data-en-standard="${standardKey}"]`
    );

    if (resetButtons.length) {
      resetButtons.forEach((btn) => {
        const wrapper = btn.closest(".en-reset-wrapper");
        if (wrapper) wrapper.classList.toggle("active", hasActive);
      });
    } else if (standardKey === "en388") {
      document
        .querySelectorAll(".en388-reset-wrapper")
        .forEach((w) => w.classList.toggle("active", hasActive));
    }

    // ✅ Open/close ALL fake groups for this standard (sidebar + drawer)
    const parentWrappers = Array.from(
      document.querySelectorAll(
        `[data-en-parent-group="true"][data-en-standard="${standardKey}"]`
      )
    );

    // Legacy EN388 fallback if needed
    if (!parentWrappers.length && standardKey === "en388") {
      parentWrappers.push(...document.querySelectorAll(".en388-parent-wrapper"));
    }

    const hasFakeActive = !!document.querySelector(
      `.en-parent-btn[data-en-standard="${standardKey}"].active`
    );

    const shouldOpen = hasActive || hasFakeActive;

    parentWrappers.forEach((parentWrapper) => {
      const list =
        parentWrapper.querySelector(`[data-en-list="true"][data-en-standard="${standardKey}"]`) ||
        parentWrapper.querySelector(".en-parent-list") ||
        parentWrapper.querySelector(".en388-parent-list");

      const toggle =
        parentWrapper.querySelector(`[data-en-toggle="true"][data-en-standard="${standardKey}"]`) ||
        parentWrapper.querySelector(".en-parent-toggle") ||
        parentWrapper.querySelector(".en388-parent-toggle");

      if (!list) return;

      if (shouldOpen) {
        // Open
        list.classList.add("is-open");
        list.setAttribute("aria-hidden", "false");

        // Ensure height isn't stuck at 0 from server inline styles
        const h = list.scrollHeight;
        list.style.height = `${h}px`;
        setTimeout(() => {
          if (list.classList.contains("is-open")) list.style.height = "auto";
        }, 300);

        if (toggle) {
          toggle.classList.add("is-open");
          toggle.setAttribute("aria-expanded", "true");
        }
      } else {
        // Close
        list.classList.remove("is-open");
        list.setAttribute("aria-hidden", "true");
        list.style.height = "0px";

        if (toggle) {
          toggle.classList.remove("is-open");
          toggle.setAttribute("aria-expanded", "false");
        }
      }
    });
  }

  /* =========================================================
     INIT + HIDE REAL SHOPIFY GROUPS for a standard
  ========================================================= */
  function initStandard(standardKey) {
    const st = state[standardKey];
    st.children = [];

    const token = TOKENS_BY_KEY[standardKey];

    document.querySelectorAll("[data-filter-group]").forEach((group) => {
      // Skip custom parent wrappers
      if (group.getAttribute("data-en-parent-group") === "true") return;
      if (group.hasAttribute("data-en388-parent-group")) return;

      const groupToken = getGroupToken(group);

      // ✅ minimal but robust match: token must appear somewhere in group id
      if (!groupToken.includes(token)) return;

      group.classList.add("en-child-group");
      group.dataset.enStandard = standardKey;

      // Hide original Shopify EN group by default
      group.style.display = "none";

      st.children.push(group);
    });
  }

  function hideOriginalStandard(standardKey) {
    const token = TOKENS_BY_KEY[standardKey];

    document.querySelectorAll("[data-filter-group]").forEach((group) => {
      // Don’t hide custom parent wrappers
      if (group.getAttribute("data-en-parent-group") === "true") return;
      if (group.hasAttribute("data-en388-parent-group")) return;

      const groupToken = getGroupToken(group);
      if (!groupToken.includes(token)) return;

      group.style.display = "none";
    });
  }

  /* =========================================================
     AUTO OPEN ACTIVE CHILD GROUPS (URL / ajax state)
  ========================================================= */
  function autoOpenActiveStandard(standardKey) {
    const st = state[standardKey];
    const token = TOKENS_BY_KEY[standardKey];

    document
      .querySelectorAll("[data-filter-group-has-active-filters='true']")
      .forEach((child) => {
        const childToken = getGroupToken(child);
        if (!childToken.includes(token)) return;

        // Show child
        child.style.display = "block";

        const wrapper = child.querySelector(".filter-form__group-filter-wrapper");
        const toggle = child.querySelector("[data-filter-group-toggle]");

        if (wrapper) {
          wrapper.style.display = "block";
          wrapper.setAttribute("aria-hidden", "false");
        }
        if (toggle) toggle.setAttribute("aria-expanded", "true");

        // Sync fake parent buttons for this standard
        const genericFakeBtns = document.querySelectorAll(
          `.en-parent-btn[data-en-standard="${standardKey}"]`
        );
        const fallbackEn388Btns =
          standardKey === "en388" ? document.querySelectorAll(".en388-parent-btn") : [];

        const buttonsToCheck = genericFakeBtns.length ? genericFakeBtns : fallbackEn388Btns;

        buttonsToCheck.forEach((fakeBtn) => {
          const fakeTarget = normalize(
            fakeBtn.dataset.enTarget || fakeBtn.dataset.en388Target || ""
          );

          // ✅ Compare fake target to the child's group token (same source as init/hide)
          if (fakeTarget !== childToken) return;

          if (fakeBtn.dataset.manualOff === "true") return;

          fakeBtn.classList.add("active");
          const checkbox =
            fakeBtn.querySelector("input[data-en-input]") ||
            fakeBtn.querySelector("input[data-en388-input]");
          if (checkbox) checkbox.checked = true;
        });
      });

    updateResetVisibility(standardKey);
  }

  /* =========================================================
     PARENT GROUP COLLAPSE TOGGLE (unchanged)
  ========================================================= */
  document.addEventListener("click", (e) => {
    const genericToggle = e.target.closest("[data-en-toggle='true']");
    const legacyToggle = e.target.closest(".en388-parent-toggle");
    const toggleBtn = genericToggle || legacyToggle;
    if (!toggleBtn) return;

    const wrapper =
      toggleBtn.closest("[data-en-parent-group='true']") ||
      toggleBtn.closest("[data-en388-parent-group]");
    if (!wrapper) return;

    const standardKey =
      toggleBtn.dataset.enStandard ||
      wrapper.dataset.enStandard ||
      (wrapper.hasAttribute("data-en388-parent-group") ? "en388" : null);
    if (!standardKey) return;

    const list =
      wrapper.querySelector(`[data-en-list='true'][data-en-standard='${standardKey}']`) ||
      wrapper.querySelector(".en388-parent-list") ||
      wrapper.querySelector(".en-parent-list");
    if (!list) return;

    const isOpen = list.classList.contains("is-open");

    if (isOpen) {
      const fullHeight = list.scrollHeight;
      list.style.height = `${fullHeight}px`;

      requestAnimationFrame(() => {
        list.style.height = "0px";
        list.classList.remove("is-open");
        list.setAttribute("aria-hidden", "true");
      });

      toggleBtn.classList.remove("is-open");
    } else {
      list.classList.add("is-open");
      list.setAttribute("aria-hidden", "false");
      toggleBtn.classList.add("is-open");

      const fullHeight = list.scrollHeight;
      list.style.height = "0px";

      requestAnimationFrame(() => {
        list.style.height = `${fullHeight}px`;
      });

      setTimeout(() => {
        if (list.classList.contains("is-open")) list.style.height = "auto";
      }, 300);
    }
  });

  /* =========================================================
     FAKE PARENT CLICK (same logic, now matches child groups using getGroupToken)
  ========================================================= */
  document.addEventListener(
    "click",
    (event) => {
      const btn = event.target.closest("label.en-parent-btn");
      const legacyBtn = event.target.closest("label.en388-parent-btn");
      const targetBtn = btn || legacyBtn;
      if (!targetBtn) return;

      event.preventDefault();
      event.stopPropagation();

      const standardKey =
        targetBtn.dataset.enStandard ||
        (targetBtn.classList.contains("en388-parent-btn") ? "en388" : null);
      if (!standardKey) return;

      const st = state[standardKey];

      const target = normalize(targetBtn.dataset.enTarget || targetBtn.dataset.en388Target);
      const isActive = targetBtn.classList.contains("active");

      // OFF
      if (isActive) {
        targetBtn.classList.remove("active");
        targetBtn.dataset.manualOff = "true";

        const fakeInput =
          targetBtn.querySelector("input[data-en-input]") ||
          targetBtn.querySelector("input[data-en388-input]");
        if (fakeInput) fakeInput.checked = false;

        st.children.forEach((child) => {
          const childToken = getGroupToken(child);
          if (childToken !== target) return;

          child.style.display = "none";

          const wrap = child.querySelector(".filter-form__group-filter-wrapper");
          const tog = child.querySelector("[data-filter-group-toggle]");

          if (wrap) {
            wrap.style.display = "none";
            wrap.setAttribute("aria-hidden", "true");
          }
          if (tog) tog.setAttribute("aria-expanded", "false");

          const clearBtn = child.querySelector("button[data-filter-group-clear]");
          if (clearBtn) clearBtn.click();
        });

        updateResetVisibility(standardKey);
        return;
      }

      // ON
      targetBtn.classList.add("active");
      targetBtn.dataset.manualOff = "false";

      const fakeInput =
        targetBtn.querySelector("input[data-en-input]") ||
        targetBtn.querySelector("input[data-en388-input]");
      if (fakeInput) fakeInput.checked = true;

      st.children.forEach((child) => {
        const childToken = getGroupToken(child);
        if (childToken !== target) return;

        child.style.display = "block";

        const wrap = child.querySelector(".filter-form__group-filter-wrapper");
        const tog = child.querySelector("[data-filter-group-toggle]");

        if (wrap) {
          wrap.style.display = "block";
          wrap.setAttribute("aria-hidden", "false");
        }
        if (tog) tog.setAttribute("aria-expanded", "true");

        setTimeout(() => {
          const rect = child.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

          window.scrollTo({
            top: rect.top + scrollTop - 100,
            behavior: "smooth",
          });
        }, 50);
      });

      updateResetVisibility(standardKey);
    },
    true
  );

  /* =========================================================
     RESET BUTTON CLICK (unchanged, but uses EN_STANDARDS keys)
  ========================================================= */
  document.addEventListener("click", (e) => {
    const resetBtn = e.target.closest("[data-en-reset='true']");
    const legacyReset = e.target.closest(".en388-reset-btn");
    const btn = resetBtn || legacyReset;
    if (!btn) return;

    const standardKey =
      btn.dataset.enStandard || (btn.classList.contains("en388-reset-btn") ? "en388" : null);
    if (!standardKey) return;

    const st = state[standardKey];

    const now = Date.now();
    if (st.isProcessing || now < st.nextAllowed) return;

    st.isProcessing = true;
    st.nextAllowed = now + 300;

    const fakeBtns = document.querySelectorAll(
      `.en-parent-btn[data-en-standard="${standardKey}"].active`
    );
    const legacyEn388Btns =
      standardKey === "en388" ? document.querySelectorAll(".en388-parent-btn.active") : [];

    const buttonsToClear = fakeBtns.length ? fakeBtns : legacyEn388Btns;

    buttonsToClear.forEach((b) => {
      b.classList.remove("active");
      b.dataset.manualOff = "true";
      const cb = b.querySelector("input[data-en-input]") || b.querySelector("input[data-en388-input]");
      if (cb) cb.checked = false;
    });

    st.children.forEach((group) => {
      group.style.display = "none";

      const wrap = group.querySelector(".filter-form__group-filter-wrapper");
      const tog = group.querySelector("[data-filter-group-toggle]");

      if (wrap) {
        wrap.style.display = "none";
        wrap.setAttribute("aria-hidden", "true");
      }
      if (tog) tog.setAttribute("aria-expanded", "false");
    });

    const resetButtons = document.querySelectorAll(
      `[data-en-reset='true'][data-en-standard='${standardKey}']`
    );

    if (resetButtons.length) {
      resetButtons.forEach((btnEl) => {
        const wrapper = btnEl.closest(".en-reset-wrapper");
        if (wrapper) wrapper.classList.remove("active");
      });
    } else if (standardKey === "en388") {
      document
        .querySelectorAll(".en388-reset-wrapper")
        .forEach((w) => w.classList.remove("active"));
    }

    let activeGroup = null;
    st.children.forEach((group) => {
      if (group.querySelector("input[type='checkbox']:checked")) activeGroup = group;
    });
    if (!activeGroup) activeGroup = st.children[0];

    const clearBtn = activeGroup?.querySelector("button[data-filter-group-clear]");
    if (clearBtn) clearBtn.click();

    setTimeout(() => {
      st.isProcessing = false;
    }, 400);
  });

  /* =========================================================
     CHILD FILTER CHANGE → update reset visibility (per standard)
  ========================================================= */
  document.addEventListener("change", (e) => {
    const input = e.target.closest(".en-child-group input[type='checkbox']");
    if (!input) return;

    const group = input.closest(".en-child-group");
    const standardKey = group?.dataset?.enStandard;
    if (!standardKey) return;

    updateResetVisibility(standardKey);
  });

  /* =========================================================
     INITIAL RUN
  ========================================================= */
  EN_STANDARDS.forEach(({ key }) => {
    initStandard(key);
    hideOriginalStandard(key);
    autoOpenActiveStandard(key);
    updateResetVisibility(key);
    openFakeParentIfActive(key);
  });

  /* =========================================================
     SHOPIFY AJAX FILTER UPDATE
  ========================================================= */
  document.body.addEventListener("collection:filters-updated", () => {
    EN_STANDARDS.forEach(({ key }) => {
      const st = state[key];
      const now = Date.now();
      if (st.isProcessing || now < st.nextAllowed) return;

      st.isProcessing = true;
      st.nextAllowed = now + 300;

      setTimeout(() => {
        initStandard(key);
        hideOriginalStandard(key);
        autoOpenActiveStandard(key);
        updateResetVisibility(key);
        openFakeParentIfActive(key);

        setTimeout(() => {
          st.isProcessing = false;
        }, 200);
      }, 60);
    });
  });
});